import React, {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import TableBody from '@material-ui/core/TableBody';
import BigNumber from 'bignumber.js';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import WaiveTableRow from './WaiveTableRow';
import {WaiveType} from '../../../../../../../shared/model/consolidate-loans';
import {CBSLoanDetails} from '../../../../../../../shared/model/cbs-loans';
import {CBSFee} from '../../../../../../../shared/model/cbs-fee';
import {WaiveInputContext} from './waive-input-context';
import {CashHelper} from '../../../../../../../utils/cash-helper';

const CONSOLIDATED_LOANS_PATH = 'LOAN_CONFIGURATIONS.LOAN_APPLICATION_DATA.CONSOLIDATED_LOANS';
const MAX_ROW_NUMBER_WITHOUT_CUSTOM_FEES = 8;

interface Props {
  cbsLoanDetails: CBSLoanDetails;
  customFees: CBSFee[];
}

const WaiveTableBody = ({cbsLoanDetails, customFees}: Props): React.ReactElement => {
  const {t} = useTranslation();
  const {handleWaiveInputValueChange, waiveInputs} = useContext(WaiveInputContext);

  const onInputChange = (value?: number | null | undefined, waiveType?: WaiveType, feeDefinitionId?: number): void => {
    handleWaiveInputValueChange(value, cbsLoanDetails.loanId, waiveType, feeDefinitionId);
  };

  const loanWaiveInputs = useMemo(() => {
    return waiveInputs.filter(waive => waive.loanId === cbsLoanDetails.loanId);
  }, [waiveInputs])

  const groupedWaiveInputs = useMemo(() => {
    const groupedWaiveInputs = groupBy(loanWaiveInputs, 'waiveType');
    return groupedWaiveInputs;
  }, [loanWaiveInputs, cbsLoanDetails]);

  const getFinalBalance = (outstandingBalance: number | undefined, waiveAmount: number): number => {
    if (outstandingBalance) {
      return BigNumber(outstandingBalance).minus(waiveAmount).toNumber();
    }
    return 0;
  };

  const getCustomFeeFinalBalance = (feeDefintionId: number | undefined, waiveAmount: number): number => {
    const customFee = getCustomFee(feeDefintionId);

    if (customFee) {
      return BigNumber(customFee.balance).minus(waiveAmount).toNumber();
    }

    return 0;
  };

  const getFeeName = (feeDefinitionId: number | undefined): string => {
    return getCustomFee(feeDefinitionId)?.feeName || '';
  };

  const getCustomFee = (feeDefinitionId: number | undefined): CBSFee | undefined => {
    return customFees.find(fee => fee.feeDefinitionId === feeDefinitionId);
  };

  const getCustomFeeBalance = (feeDefinitionId: number | undefined): number => {
    return getCustomFee(feeDefinitionId)?.balance || 0;
  }

  const overallOutstandingBalance = useMemo((): number => {
    const total = BigNumber(CashHelper.getAmountOrZero(cbsLoanDetails.principalBalance))
      .plus(CashHelper.getAmountOrZero(cbsLoanDetails.interestBalance))
      .plus(CashHelper.getAmountOrZero(cbsLoanDetails.cbuAmount))
      .plus(CashHelper.getAmountOrZero(cbsLoanDetails.pfAmount))
      .plus(CashHelper.getAmountOrZero(cbsLoanDetails.tpAmount))
      .plus(CashHelper.getAmountOrZero(cbsLoanDetails.pastDueInterestAmount))
      .plus(CashHelper.getAmountOrZero(cbsLoanDetails.penaltiesAmount));
    if (!isEmpty(customFees)) {
      const totalCustomFeeBalance = customFees.map(fee => fee.balance).reduce((acc, curr) => {
        return acc.plus(curr);
      }, new BigNumber(0));
      return total.plus(totalCustomFeeBalance).toNumber();
    }
    return total.toNumber();
  }, [cbsLoanDetails]);

  const overallFinalBalance = useMemo((): number => {
    const overallFinalBalance = loanWaiveInputs.map(waive => {
      switch (waive.waiveType) {
        case WaiveType.INTEREST:
          return getFinalBalance(cbsLoanDetails.interestBalance, waive.waiveAmount);
        case WaiveType.CBU:
          return getFinalBalance(cbsLoanDetails.cbuAmount, waive.waiveAmount);
        case WaiveType.PF:
          return getFinalBalance(cbsLoanDetails.pfAmount, waive.waiveAmount);
        case WaiveType.TP:
          return getFinalBalance(cbsLoanDetails.tpAmount, waive.waiveAmount);
        case WaiveType.PAST_DUE_INTEREST:
          return getFinalBalance(cbsLoanDetails.pastDueInterestAmount, waive.waiveAmount);
        case WaiveType.PENALTY:
          return getFinalBalance(cbsLoanDetails.penaltiesAmount, waive.waiveAmount);
        case WaiveType.CUSTOM_FEE:
          return getCustomFeeFinalBalance(waive.feeDefinitionId, waive.waiveAmount);
        default:
          return 0;
      }
    }).reduce((acc, curr) => {
      return acc.plus(curr);
    }, new BigNumber(0));

    return overallFinalBalance.plus(cbsLoanDetails.principalBalance || 0).toNumber();
  }, [cbsLoanDetails, loanWaiveInputs]);

  const totalWaiveInput = useMemo((): number => {
    return loanWaiveInputs.map(waive => waive.waiveAmount)
      .reduce((acc, curr) => acc.plus(curr), new BigNumber(0))
      .toNumber();
  }, [loanWaiveInputs]);

  const getFooterRowNumber = (): number =>
    MAX_ROW_NUMBER_WITHOUT_CUSTOM_FEES + ((groupedWaiveInputs[WaiveType.CUSTOM_FEE] && groupedWaiveInputs[WaiveType.CUSTOM_FEE].length) || 0);

  return (
    <TableBody>
      <WaiveTableRow rowNumber={1}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_PRINCIPAL`)}
                     finalBalance={cbsLoanDetails.principalBalance || 0}
                     outstandingBalance={cbsLoanDetails.principalBalance || 0}
                     waiveInput={0}
                     isEditable={false} />
      {cbsLoanDetails.asEarnedPreterminationInterestCalculation ? (
        <WaiveTableRow rowNumber={2}
                       feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_INTEREST_AS_EARNED`)}
                       finalBalance={getFinalBalance(cbsLoanDetails.interestBalance,
                         groupedWaiveInputs[WaiveType.INTEREST][0].waiveAmount)}
                       outstandingBalance={cbsLoanDetails.interestBalance || 0}
                       waiveInput={groupedWaiveInputs[WaiveType.INTEREST][0].waiveAmount}
                       onWaiveInputChange={onInputChange}
                       waiveType={WaiveType.INTEREST} />) : (
        <WaiveTableRow rowNumber={2}
                       feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_INTEREST`)}
                       finalBalance={getFinalBalance(cbsLoanDetails.interestBalance,
                         groupedWaiveInputs[WaiveType.INTEREST][0].waiveAmount)}
                       outstandingBalance={cbsLoanDetails.interestBalance || 0}
                       waiveInput={groupedWaiveInputs[WaiveType.INTEREST][0].waiveAmount}
                       onWaiveInputChange={onInputChange}
                       waiveType={WaiveType.INTEREST} />)}
      <WaiveTableRow rowNumber={3}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_CBU`)}
                     finalBalance={getFinalBalance(cbsLoanDetails.cbuAmount,
                       groupedWaiveInputs[WaiveType.CBU][0].waiveAmount)}
                     outstandingBalance={cbsLoanDetails.cbuAmount}
                     waiveInput={groupedWaiveInputs[WaiveType.CBU][0].waiveAmount}
                     onWaiveInputChange={onInputChange}
                     waiveType={WaiveType.CBU} />
      <WaiveTableRow rowNumber={4}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_PF`)}
                     finalBalance={getFinalBalance(cbsLoanDetails.pfAmount,
                       groupedWaiveInputs[WaiveType.PF][0].waiveAmount)}
                     outstandingBalance={cbsLoanDetails.pfAmount}
                     waiveInput={groupedWaiveInputs[WaiveType.PF][0].waiveAmount}
                     onWaiveInputChange={onInputChange}
                     waiveType={WaiveType.PF} />
      <WaiveTableRow rowNumber={5}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_TP`)}
                     finalBalance={getFinalBalance(cbsLoanDetails.tpAmount,
                       groupedWaiveInputs[WaiveType.TP][0].waiveAmount)}
                     outstandingBalance={cbsLoanDetails.tpAmount}
                     waiveInput={groupedWaiveInputs[WaiveType.TP][0].waiveAmount}
                     onWaiveInputChange={onInputChange}
                     waiveType={WaiveType.TP} />
      <WaiveTableRow rowNumber={6}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_PDI`)}
                     finalBalance={getFinalBalance(cbsLoanDetails.pastDueInterestAmount,
                       groupedWaiveInputs[WaiveType.PAST_DUE_INTEREST][0].waiveAmount)}
                     outstandingBalance={cbsLoanDetails.pastDueInterestAmount}
                     waiveInput={groupedWaiveInputs[WaiveType.PAST_DUE_INTEREST][0].waiveAmount}
                     onWaiveInputChange={onInputChange}
                     waiveType={WaiveType.PAST_DUE_INTEREST} />
      <WaiveTableRow rowNumber={7}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_PENALTIES`)}
                     finalBalance={getFinalBalance(cbsLoanDetails.penaltiesAmount,
                       groupedWaiveInputs[WaiveType.PENALTY][0].waiveAmount)}
                     outstandingBalance={cbsLoanDetails.penaltiesAmount}
                     waiveInput={groupedWaiveInputs[WaiveType.PENALTY][0].waiveAmount}
                     onWaiveInputChange={onInputChange}
                     waiveType={WaiveType.PENALTY} />
      {groupedWaiveInputs[WaiveType.CUSTOM_FEE] && groupedWaiveInputs[WaiveType.CUSTOM_FEE].map((waive, index) => (
        <WaiveTableRow rowNumber={MAX_ROW_NUMBER_WITHOUT_CUSTOM_FEES + index}
                       key={`custom-fee-${index}-${waive.feeDefinitionId}`}
                       feeName={getFeeName(waive.feeDefinitionId)}
                       finalBalance={getCustomFeeFinalBalance(waive.feeDefinitionId, waive.waiveAmount)}
                       outstandingBalance={getCustomFeeBalance(waive.feeDefinitionId)}
                       waiveInput={waive.waiveAmount}
                       onWaiveInputChange={onInputChange}
                       feeDefinitionId={waive.feeDefinitionId}
                       waiveType={WaiveType.CUSTOM_FEE} />
      ))}
      <WaiveTableRow rowNumber={getFooterRowNumber()}
                     feeName={t(`${CONSOLIDATED_LOANS_PATH}.TOTAL_AMOUNT`)}
                     finalBalance={overallFinalBalance}
                     outstandingBalance={overallOutstandingBalance}
                     waiveInput={totalWaiveInput}
                     isEditable={false}
                     onWaiveInputChange={onInputChange} />
    </TableBody>
  );
};

export default WaiveTableBody;
